import { action } from "typesafe-actions";
import { ThunkAction } from "redux-thunk";
import { IStoreState } from "../../initialStoreState";
import { AnyAction } from "redux";
import { api } from "../../../api/api";
import {
  saveLoaderCompleted,
  saveLoaderProgress,
  showMessage,
} from "../../messages/messagesActions";

import { getFileExtensionFromUrl } from "../../../helpers";
import { ITrialBalance } from "./TrialBalance.types";
import { translateBackendError } from "../../../helpers/errorTranslator";

export const FETCH_TRIAL_BALANCE_LIST_PROGRESS =
  "FETCH_TRIAL_BALANCE_LIST_PROGRESS";
export const FETCH_TRIAL_BALANCE_LIST_SUCCESS =
  "FETCH_TRIAL_BALANCE_LIST_SUCCESS";
export const FETCH_TRIAL_BALANCE_LIST_FAILED =
  "FETCH_TRIAL_BALANCE_LIST_FAILED";

export const fetchTrialBalanceListProgress = () =>
  action(FETCH_TRIAL_BALANCE_LIST_PROGRESS);
export const fetchTrialBalanceListSuccess = (
  data: ITrialBalance[],
  totalRecords: number
) =>
  action(FETCH_TRIAL_BALANCE_LIST_SUCCESS, { data, totalRecords });
export const fetchTrialBalanceListFailed = () =>
  action(FETCH_TRIAL_BALANCE_LIST_FAILED);

/**
 * Optional branch-filter payload for the Trial Balance fetch thunks.
 *
 * Exactly one of `branch_codes` / `exclude_branch_codes` should be populated
 * at a time (the include/exclude toggle on the {@link BranchPicker} guarantees
 * this on the UI side). Each is serialized as REPEATED query params, e.g.
 * `&branch_codes=A&branch_codes=B`. An empty/omitted selection sends neither
 * param, which the backend treats as the whole company.
 */
export interface ITrialBalanceBranchFilter {
  branch_codes?: string[];
  exclude_branch_codes?: string[];
}

/**
 * Appends `branch_codes` / `exclude_branch_codes` as REPEATED query params to
 * an already-started query string (which must already contain at least one
 * param so the caller controls the leading `?`). Empty arrays are skipped.
 */
const appendBranchParams = (
  branchFilter: ITrialBalanceBranchFilter | undefined
): string => {
  if (!branchFilter) return "";
  const parts: string[] = [];
  (branchFilter.branch_codes ?? []).forEach((code) => {
    if (code) parts.push(`branch_codes=${encodeURIComponent(code)}`);
  });
  (branchFilter.exclude_branch_codes ?? []).forEach((code) => {
    if (code) parts.push(`exclude_branch_codes=${encodeURIComponent(code)}`);
  });
  return parts.length > 0 ? `&${parts.join("&")}` : "";
};

export const fetchTrialBalanceListAsync =
  (
    status: string,
    isZeroInclude: boolean,
    searchState: { [key: string]: any },
    branchFilter?: ITrialBalanceBranchFilter
  ): ThunkAction<void, IStoreState, {}, AnyAction> =>
  async (dispatch, getState) => {
    try {
      const { month, year, to_date  } = searchState;
      dispatch(fetchTrialBalanceListProgress());
       let finalUrl = `/accounting/get-trail-balance-sheet?to_date=${to_date}`;
      //let finalUrl = `/accounting/get-trail-balance-sheet?month=${month}&year=${year}`;
      // if(isZeroInclude){
      //   finalUrl += "&isZeroInclude=" + isZeroInclude
      // }

      if (status !== "-1") {
        finalUrl += "&status=" + status;
      }

      finalUrl += appendBranchParams(branchFilter);

      const res = await api.get(finalUrl);
      const data: ITrialBalance[] = res.data.data.trialBalance;
      const totalRecords = res.data.data.trialBalance.length;

      dispatch(fetchTrialBalanceListSuccess(data, totalRecords));
    } catch (err: any) {
      dispatch(fetchTrialBalanceListFailed());
      dispatch(
        showMessage({
          type: "error",
          message: translateBackendError(err.response?.data),
          displayAs: "snackbar",
        })
      );
    }
  };

export const fetchTrialBalanceListForExportAsync =
  (
    searchState: { [key: string]: any },
    userId: number,
    onCallback: (isSuccess: boolean) => void,
    branchFilter?: ITrialBalanceBranchFilter
  ): ThunkAction<void, IStoreState, {}, AnyAction> =>
  async (dispatch, getState) => {
    try {
        const { to_date } = searchState;
      dispatch(saveLoaderProgress());

      const finalUrl =
        `/accounting/get-trail-balance-sheet-pdf?to_date=${to_date}&user_id=${userId}` +
        appendBranchParams(branchFilter);

      // if (from_date) {
      //   const filterObj = {
      //     column: ["date"],
      //     operator: "GREATER_THAN_EQUAL",
      //     value: from_date,
      //     logicalOperator: "AND",
      //   };

      //   const encodedFilter = encodeURIComponent(
      //     JSON.stringify(filterObj, null, 2)
      //   );
      //   finalUrl += `&advanceFilter=${encodedFilter}`;
      // }
      // if (to_date) {
      //   const filterObj = {
      //     column: ["date"],
      //     operator: "LESSER_THAN_EQUAL",
      //     value: to_date,
      //     logicalOperator: "AND",
      //   };

      //   const encodedFilter = encodeURIComponent(
      //     JSON.stringify(filterObj, null, 2)
      //   );
      //   finalUrl += `&advanceFilter=${encodedFilter}`;
      // }

      const res = await api.get(finalUrl, {
        responseType: "arraybuffer",
      });

      const blob = new Blob([res.data], {
        type: "application/" + getFileExtensionFromUrl("Balance_sheet.pdf"),
      });
      // const blob = new Blob([res.data], { type: "application/octet-stream" });
      const url = window.URL.createObjectURL(blob);

      // Create a link element and simulate a click to trigger the download
      const link = document.createElement("a");
      link.href = url;
     link.setAttribute("download", "Balance_sheet.pdf");// Set the desired file name and extension
      document.body.appendChild(link);
      link.click();

      onCallback(true);
    } catch (err: any) {
      onCallback(false);
      dispatch(
        showMessage({
          type: "error",
          message: translateBackendError(err.response?.data),
          displayAs: "snackbar",
        })
      );
    } finally {
      dispatch(saveLoaderCompleted());
    }
  };

export const CLEAR_TRIAL_BALANCE = "CLEAR_TRIAL_BALANCE";
export const clearTrialBalances = () =>
  action(CLEAR_TRIAL_BALANCE);
